/**
 * Build Vacasa API search request from SearchFilters
 * Used by both rental.repository.ts and API endpoints
 */
import type {SearchFilters} from "@/entities/rental/types";

import {DateHelper} from "@/shared/lib";

import type {VacasaSearchRequest} from "../types/search.types";
import {isLocationCity} from "./is-location-city";

/**
 * Transform SearchFilters to Vacasa API request format
 * All params use camelCase as expected by Vacasa API
 */
export function buildSearchRequest(filters: SearchFilters): VacasaSearchRequest {
    const request: VacasaSearchRequest = {};

    // Location slug
    if (filters.location) {
        request.slug = filters.location;
    }

    // Add nearbyCities flag when searching within a specific city
    if (isLocationCity(filters.location)) {
        request.nearbyCities = true;
    }

    // Dates (checkIn, checkOut - YYYY-MM-DD)
    if (filters.startDate) {
        request.checkIn = typeof filters.startDate === "string" ? filters.startDate : DateHelper.formatDate(filters.startDate);
    }
    if (filters.endDate) {
        request.checkOut = typeof filters.endDate === "string" ? filters.endDate : DateHelper.formatDate(filters.endDate);
    }

    // Flexible dates
    if (filters.flexible !== undefined) {
        request.flexible = filters.flexible;
    }
    if (filters.nights !== undefined) {
        request.nights = filters.nights;
    }

    // Guests
    if (filters.adults !== undefined) {
        request.adults = filters.adults;
    }
    if (filters.children !== undefined) {
        request.children = filters.children;
    }
    if (filters.pets !== undefined) {
        request.pets = filters.pets;
    }

    // Bedrooms (camelCase: minBeds, maxBeds)
    if (filters.minBeds !== undefined) {
        request.minBeds = filters.minBeds;
    }
    if (filters.maxBeds !== undefined) {
        request.maxBeds = filters.maxBeds;
    }

    // Bathrooms (camelCase: minBaths, maxBaths)
    if (filters.minBaths !== undefined) {
        request.minBaths = filters.minBaths;
    }
    if (filters.maxBaths !== undefined) {
        request.maxBaths = filters.maxBaths;
    }

    // Nightly rate (camelCase: minRate, maxRate)
    if (filters.minRate !== undefined) {
        request.minRate = filters.minRate;
    }
    if (filters.maxRate !== undefined) {
        request.maxRate = filters.maxRate;
    }

    // Housing types (array: ["condo", "house"])
    if (filters.housingTypes?.length) {
        request.housingType = filters.housingTypes;
    }

    // Amenities (array: ["hottub", "pool"])
    if (filters.amenities?.length) {
        request.amenities = filters.amenities;
    }

    // Map bounds (camelCase)
    if (filters.neLat !== undefined) {
        request.neLat = filters.neLat;
    }
    if (filters.neLng !== undefined) {
        request.neLng = filters.neLng;
    }
    if (filters.swLat !== undefined) {
        request.swLat = filters.swLat;
    }
    if (filters.swLng !== undefined) {
        request.swLng = filters.swLng;
    }

    // Map center and zoom
    if (filters.centerLat !== undefined) {
        request.centerLat = filters.centerLat;
    }
    if (filters.centerLng !== undefined) {
        request.centerLng = filters.centerLng;
    }
    if (filters.zoom !== undefined) {
        request.zoom = filters.zoom;
    }

    return request;
}
